import { useMemo } from 'react';
import { useAuthorizationStore } from '@/stores/authorization';
import { normalizeClientRoutePath } from '@/stores/authorization';
import { useAuthStore } from '@/stores/auth';
import type { FunctionalityPermission } from '@/stores/authorization';
import { buildPayloadMenuTree, type PayloadMenuItem } from './payloadMenuTree';
import { AiFillHome } from 'react-icons/ai';
import {
  BsBarChartFill,
  BsCalendar2WeekFill,
  BsCameraVideoFill,
  BsJournalText,
  BsFillShieldLockFill,
  BsPlugFill,
} from 'react-icons/bs';
import {
  Activity,
  Building2,
  CalendarClock,
  ClipboardCheck,
  Stethoscope,
  UserRoundSearch,
  Wrench,
} from 'lucide-react';
import { MdBusiness } from 'react-icons/md';
import { useCustomReportNavigation } from '@/hooks/useCustomReportNavigation';

export type { PayloadMenuItem } from './payloadMenuTree';

const FUNC_ICONS: Record<string, React.ElementType> = {
  // Apenas pais (root level de cada módulo) têm ícone.
  // Filhos (itens aninhados) renderizam só como texto — ícone ignorado.

  // Administrativo — pais
  admin_home: AiFillHome,
  admin_access_control: BsFillShieldLockFill,
  admin_organization: Building2,
  admin_establishments: Building2,
  admin_technical_staff: Stethoscope,
  admin_integrations: BsPlugFill,
  admin_companies: MdBusiness,
  admin_tools: Wrench,
  // Estabelecimento — pais
  est_home: AiFillHome,
  est_dashboard: BsBarChartFill,
  est_scale_management: BsCalendar2WeekFill,
  est_access_control: BsFillShieldLockFill,
  est_record_management_group: ClipboardCheck,
  est_structure: Building2,
  est_technical_staff: Stethoscope,
  est_teleattendance: BsCameraVideoFill,
  est_reports: BsJournalText,
  est_onboarding_interviews: CalendarClock,
  est_recruitment: UserRoundSearch,
  est_tools: Wrench,
  // Companhia Médica — pais
  company_home: AiFillHome,
  company_access_control: BsFillShieldLockFill,
  company_structure: Building2,
  company_technical_staff: Stethoscope,
  company_scale: BsCalendar2WeekFill,
  company_reports: BsJournalText,
  company_onboarding_interviews: CalendarClock,
  company_recruitment: UserRoundSearch,
  // Gestão Médica — pais
  mgmt_home: Activity,
};

const EMPTY_FUNCTIONALITIES: FunctionalityPermission[] = [];
const MEDICAL_COMPANY_MODULE_KEY = 'medical_company';
const MEDICAL_COMPANY_HOME_KEY = 'company_home';

export function usePayloadMenu(): PayloadMenuItem[] {
  const selectedModuleKey = useAuthorizationStore((s) => s.selectedModuleKey);
  const modules = useAuthorizationStore((s) => s.payload?.modules);
  const functionalities = useAuthorizationStore(
    (s) => s.payload?.functionalities ?? EMPTY_FUNCTIONALITIES,
  );
  const companyConfigId = useAuthStore((s) => s.user?.company_config_id);
  const customReports = useCustomReportNavigation();

  return useMemo(() => {
    // Resolve effective module: selected, or auto-fallback when only 1 module
    const effectiveModuleKey =
      selectedModuleKey ?? (modules?.length === 1 ? modules[0].key : null);

    if (!effectiveModuleKey) return [];
    const moduleFuncs = [...functionalities, ...customReports].filter((f) => {
      if (f.module_key !== effectiveModuleKey) return false;
      if (
        effectiveModuleKey === MEDICAL_COMPANY_MODULE_KEY &&
        !companyConfigId
      ) {
        return f.key === MEDICAL_COMPANY_HOME_KEY;
      }
      return true;
    });
    return buildPayloadMenuTree(moduleFuncs, normalizeClientRoutePath, FUNC_ICONS);
  }, [selectedModuleKey, modules, functionalities, companyConfigId, customReports]);
}
